import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Lock, CreditCard, Loader2, FlaskConical, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { ProfileTab, SecurityTab, PaymentTab, TestModeTab, TwoFactorTab, PrivacyTab } from '../components/settings';

export default function Settings() {
  const { isArtist, isAdmin, isAgent, testMode } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  // While an admin is in test mode they're viewing as a persona — the persona shouldn't
  // see the admin-only Test Mode tab, but the admin's REAL account still has access via
  // the Exit Test Mode banner. Hide the tab unless the user is truly an admin.
  const showTestModeTab = isAdmin && !testMode?.active;
  // Show the Manage 2FA tab only to real admins (not personas).
  const showTwoFactorTab = isAdmin && !testMode?.active;

  useEffect(() => {
    api.getProfile()
      .then(res => setProfile(res.data))
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- api/toast/setters are stable

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-lime" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 py-24">
        <h1 className="font-heading text-3xl font-bold mb-2">Settings</h1>
        <p className="text-[#A1A1AA] mb-8">Manage your account and preferences</p>

        <Tabs defaultValue={new URLSearchParams(window.location.search).get('tab') || 'profile'}>
          <TabsList className="bg-surface-highlight mb-6 flex-wrap">
            <TabsTrigger value="profile" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-profile">
              <User className="w-4 h-4 mr-2" />Profile
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-security">
              <Lock className="w-4 h-4 mr-2" />Security
            </TabsTrigger>
            {(isArtist || isAdmin || isAgent) && (
              <TabsTrigger value="payment" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-payment">
                <CreditCard className="w-4 h-4 mr-2" />Payment
              </TabsTrigger>
            )}
            <TabsTrigger value="privacy" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-privacy">
              <ShieldQuestion className="w-4 h-4 mr-2" />Privacy
            </TabsTrigger>
            {showTwoFactorTab && (
              <TabsTrigger value="two-factor" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-two-factor">
                <ShieldCheck className="w-4 h-4 mr-2" />Manage 2FA
              </TabsTrigger>
            )}
            {showTestModeTab && (
              <TabsTrigger value="test-mode" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black" data-testid="tab-test-mode">
                <FlaskConical className="w-4 h-4 mr-2" />Test Mode
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab profile={profile} />
          </TabsContent>

          <TabsContent value="security">
            <SecurityTab />
          </TabsContent>

          {(isArtist || isAdmin || isAgent) && (
            <TabsContent value="payment">
              <PaymentTab />
            </TabsContent>
          )}

          <TabsContent value="privacy">
            <PrivacyTab />
          </TabsContent>

          {showTwoFactorTab && (
            <TabsContent value="two-factor">
              <TwoFactorTab />
            </TabsContent>
          )}

          {showTestModeTab && (
            <TabsContent value="test-mode">
              <TestModeTab />
            </TabsContent>
          )}
        </Tabs>
      </main>
      <Footer />
    </div>
  );
}
